import { useEffect, useState } from 'react';
import type { SaleStatus, SetupLink, UserStatus } from '@sellit/shared';
import { ApiError } from '../lib/api';
import { copyText, SALE_STATUS_LABEL } from '../lib/format';
import { Icon } from '../lib/Icon';

/** Loads data once (and again when `deps` change). */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setError(null);
    load().then(
      (d) => live && setData(d),
      (err) => live && setError(err instanceof ApiError ? err.message : 'Could not load. Try again.'),
    );
    return () => {
      live = false;
    };
  }, deps);
  return { data, setData, error };
}

export function SaleStatusChip({ status }: { status: SaleStatus }) {
  return <span className={`chip sale-${status.toLowerCase()}`}>{SALE_STATUS_LABEL[status]}</span>;
}

export function UserStatusChip({ status, hasPassword }: { status: UserStatus; hasPassword: boolean }) {
  if (status === 'DISABLED') return <span className="chip user-disabled">Disabled</span>;
  if (!hasPassword) return <span className="chip user-invited">Invited</span>;
  return <span className="chip user-active">Active</span>;
}

export function CopyButton({ text, label = 'Copy link', className = 'btn' }: { text: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={className}
      onClick={async () => {
        await copyText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      <Icon name={copied ? 'check' : 'link'} size={18} /> {copied ? 'Copied!' : label}
    </button>
  );
}

/** Shows a freshly issued setup link: the admin copies it and sends it privately. */
export function SetupLinkCard({ link, name }: { link: SetupLink; name: string }) {
  const expires = new Date(link.expiresAt).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return (
    <div className="setup-link card" role="status">
      <strong>Setup link for {name}</strong>
      <p className="muted small">
        {link.emailed ? 'We emailed this link. You can also send it yourself.' : 'Send this link to them by text or email.'} It works once and
        expires {expires}. They'll choose their own password; you never see it.
      </p>
      <code className="link-box">{link.url}</code>
      <div className="actions">
        <CopyButton text={link.url} />
      </div>
    </div>
  );
}

export function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Something went wrong. Try again.';
}

export function fieldErrors(err: unknown): Record<string, string> {
  return err instanceof ApiError && err.fields ? err.fields : {};
}
